import type { AppKind } from "common/components/app-kind";
import { ConfirmModalStyle } from "common/components/confirm-modal";
import { getLocalizedString } from "common/components/localization";
import { isEmptyOrUndefined, nonNull } from "common/Support";
import { useRuntimeTheme } from "hooks/use-runtime-theme";
import type { GlideFC } from "lib/glide-fc";
import * as React from "react";
import { createPortal } from "react-dom";
import type { TwotoneIcons } from "sharedUI/ui/views/glide-icon/glide-icon";
import { GlideIcon } from "sharedUI/ui/views/glide-icon/glide-icon";
import { showCancelUploadModal } from "webapp/lib/in-app-modals";
import Img from "webapp/ui/smart/img/img";
import ConfirmModal from "../../../chrome/views/confirm-modal/confirm-modal";
import { APP_MODAL_ROOT } from "../../../lib/constants";
import { BasePicker } from "../base-picker/base-picker";
import type { FormElementProps } from "../form-element/form-element";

export type FilePickerMode = "normal" | "uploading" | "upload-failed" | "deleting";

export interface FilePickerViewProps extends FormElementProps {
    readonly appKind: AppKind;

    readonly value?: string;
    readonly valueLabel?: string;
    readonly isRequired?: boolean;
    readonly iconSource?: TwotoneIcons;
    readonly showPreview?: boolean;
    readonly deleteModalTitle?: string;
    readonly deleteModalDescription?: string;

    readonly mode?: FilePickerMode;

    readonly capture?: string;
    readonly idOverride?: string;
    readonly mimePattern?: string;
    readonly className?: string;

    readonly onClearClicked?: (ev: React.MouseEvent<HTMLDivElement>) => void;
    readonly onInputChange?: (ev: React.ChangeEvent<HTMLInputElement>) => void;
    readonly onDelete?: (shouldDelete: boolean) => void;
    readonly onCancel?: (shouldCancel: boolean) => void;
}

interface Props extends FilePickerViewProps {
    readonly progressPercentage?: number;
}

const FilePickerViewImpl: GlideFC<Props> = p => {
    const {
        value,
        valueLabel,
        title,
        mode,
        idOverride,
        mimePattern,
        showPreview,
        deleteModalTitle = "Delete File",
        deleteModalDescription = "Are you sure you want to delete this file?",
        className,
        isEnabled = true,
        capture,
        progressPercentage,
        onDelete,
        onClearClicked,
        onCancel,
        onInputChange,
        iconSource,
        appKind,
    } = p;

    const theme = useRuntimeTheme();
    const inputRef = React.createRef<HTMLInputElement>();

    const typeIcon = (): JSX.Element => {
        if (iconSource) {
            return (
                <GlideIcon
                    kind="twotone"
                    icon={iconSource}
                    iconSize={24}
                    primaryColor={theme.n500}
                    accentColor={theme.n200}
                />
            );
        } else {
            return (
                <GlideIcon
                    kind="twotone"
                    icon="tt-player-camera"
                    iconSize={24}
                    primaryColor={theme.n500}
                    accentColor={theme.n200}
                />
            );
        }
    };
    const onCancelClicked = React.useCallback(
        async (ev: React.MouseEvent<HTMLElement>) => {
            ev.preventDefault();
            ev.stopPropagation();

            onCancel?.(await showCancelUploadModal(appKind));
        },
        [appKind, onCancel]
    );

    // React.useEffect(() => {
    //     return () => {
    //         // componentWillUnmount
    //         if (mode !== "uploading") return;

    //         // onCancel?.(true);
    //     };
    // }, [mode]);

    // React.useEffect(() => {
    //     // componentDidUpdate
    //     if (inputRef.current === null) return;
    // }, [inputRef]);

    const id = "item-upload-" + (idOverride ?? Date.now());

    let portal: React.ReactNode;
    if (mode === "deleting") {
        portal = createPortal(
            <ConfirmModal
                title={deleteModalTitle}
                description={deleteModalDescription}
                accept={getLocalizedString("delete", appKind)}
                cancel={getLocalizedString("cancel", appKind)}
                onFinish={d => onDelete?.(d)}
                modalStyle={ConfirmModalStyle.Delete}
            />,
            nonNull(document.getElementById(APP_MODAL_ROOT))
        );
    }

    const isUploading = mode === "uploading";
    const hasValue = !isEmptyOrUndefined(value);
    const isPlaceholder = mode !== "normal" || !hasValue;

    let displayLabel = valueLabel;
    if (isUploading) {
        displayLabel = getLocalizedString("uploading", appKind);
    }

    let errorMessage: string | undefined;
    if (mode === "upload-failed") {
        errorMessage = getLocalizedString("uploadFailedInline", appKind);
    }

    return (
        <BasePicker
            {...p}
            className={className}
            data-test="app-file-picker"
            progressPercentage={isUploading ? progressPercentage : undefined}
            isPlaceholder={isPlaceholder}
            leftItem={
                hasValue && showPreview ? (
                    <Img className={(isUploading ? "fp-uploading" : undefined) + ` image`} src={value} />
                ) : (
                    typeIcon()
                )
            }
            errorMessage={errorMessage}
            rightItem={!isEmptyOrUndefined(value) || isUploading ? "clear" : undefined}
            rightItemOnClick={isUploading ? onCancelClicked : hasValue ? onClearClicked : undefined}>
            <label htmlFor={id}>{displayLabel}</label>
            {portal}
            <input
                ref={inputRef}
                capture={capture}
                type="file"
                id={id}
                accept={mimePattern}
                onChange={onInputChange}
                disabled={!isEnabled || mode === "uploading"}
                aria-label={`${title} file browser`}
            />
        </BasePicker>
    );
};

const FilePickerView = FilePickerViewImpl;
export default FilePickerView;
